import React, { useEffect, useState } from "react"
import { Grid, Typography } from "@mui/material"
import { Row } from "@components/VerticalTable"
import { NodeProperties } from "@features/graph/details/components/PropertiesTable"
import { deepCopy } from "@features/graph/utils/graphUtils"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { findNode } from "@utils/graph"
import { GraphStoreType } from "@features/graph/Graph"
import { Node, useStoreApi } from "reactflow"
import { GraphEditMode } from "@features/graph/graphViewModel"
import { EditType } from "@components/EditableCell"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import ScanariosAccordion from "@features/graph/details/components/ScanariosAccordion"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

type ScenarioNodeDetailsProps = {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
}

export const ScenarioNodeDetails = ({ nodeId, editMode, graphKey }: ScenarioNodeDetailsProps) => {
  const graphStore: GraphStoreType = useStoreApi()
  const [node, setNode] = useState<Node>()

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [nodeId])

  if (!node) {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const properties: NodeProperties<any> = node.data.node?.properties ?? {}
  const sampleValue = properties.a_sample_value

  const { label, color } = node.data?.display ?? {}

  const nodeRows: Row[] = [
    { label: "Type", value: "Scenario" },
    { label: "Historic Mnemonic", value: node.data?.display["Historic Mnem"] },
    { label: "Data Mnemonic", value: node.data?.display["Scenario Mnem"] },
    { label: "Description", value: node.data?.display["Description"] || "-" },
    { label: "Unit", value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-" },
    { label: "Exclude From Export", value: node.data?.display.exportExcluded ? "yes" : "no" },
    { label: "Link", value: node.data.display.Link ?? "" },
    // { label: "Color", children: <ColorPreview color={color} />, value: color },
  ]

  if (sampleValue !== null) {
    nodeRows.push({
      label: "Sample value",
      value: sampleValue,
      editable: editMode === GraphEditMode.SCENARIO,
      type: EditType.Number,
      onBlur: (newValue) => handlePropertiesFieldCellBlur("a_sample_value", newValue),
    })
  }

  const handlePropertiesFieldCellBlur = async (field: string, newValue: string) => {
    if (!graphKey) {
      return
    }

    node.data.node.properties[field] = Number(newValue)
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
        <Typography>
          This node uses the predefined value, defined by <i>'Data Mnemonic'</i> +{" "}
          <i>chosen scenario</i>.
          <br />
          <br />
          Scenarios can be viewed when running 'Sample'
        </Typography>
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      <ScanariosAccordion
        node={node}
        editable={editMode === GraphEditMode.SCENARIO}
        graphKey={graphKey}
      />
      <ParentChildDetails />
    </Grid>
  )
}
